<template>
  <section class="ncrm-privacy-card ncrm-privacy-card--compact">
    <h2 class="ncrm-privacy-card__title">{{ $t(`${copyKey}.title`, { days: periodDays }) }}</h2>
    <div class="ncrm-privacy-activity__headline">
      <span class="ncrm-privacy-card__muted">{{ $t(`${copyKey}.events`) }}</span>
      <span class="ncrm-privacy-activity__value">{{ display(activity.events) }}</span>
    </div>
    <p v-if="sourcesUnavailable" class="ncrm-privacy-card__muted">{{ $t(`${copyKey}.unavailable`) }}</p>
    <div v-else-if="sources.length" class="ncrm-privacy-activity__sources">
      <template v-for="source in sources" :key="source.key">
        <span>{{ $t(`ncrm.contacts.dataProtection.record.sources.${source.key}`) }}</span>
        <NcrmPrivacyStackedBar :segments="[source.segment]" :width-pct="source.widthPct" :height="8" />
        <strong class="ncrm-privacy-activity__count">{{ source.count }}</strong>
      </template>
    </div>
    <v-divider />
    <div class="ncrm-privacy-activity__row">
      <span class="ncrm-privacy-activity__label">
        <v-icon size="16" class="ncrm-privacy-activity__icon">mdi-account-search-outline</v-icon>
        {{ $t(`${copyKey}.reviewRequired`) }}
      </span>
      <strong>{{ display(activity.review_required) }}</strong>
    </div>
    <div class="ncrm-privacy-activity__row">
      <span class="ncrm-privacy-activity__label">
        <v-icon size="16" class="ncrm-privacy-activity__icon">mdi-sync</v-icon>
        {{ $t(`${copyKey}.mailjet`) }}
      </span>
      <span v-if="activity.mailjet_sync_errors === null" class="ncrm-privacy-card__muted">
        {{ $t(`${copyKey}.unavailableShort`) }}
      </span>
      <span v-else-if="activity.mailjet_sync_errors > 0" class="ncrm-privacy-activity__status ncrm-privacy-activity__status--error">
        <v-icon size="15">mdi-alert-circle-outline</v-icon>
        {{ $t(`${copyKey}.mailjetErrors`, { count: activity.mailjet_sync_errors }) }}
      </span>
      <span v-else class="ncrm-privacy-activity__status ncrm-privacy-activity__status--ok">
        <v-icon size="15">mdi-check</v-icon>
        {{ $t(`${copyKey}.mailjetOk`) }}
      </span>
    </div>
    <!-- S8.5b: puncte al căror ultim job de propagare Mailjet a eșuat; drill-down în Registru → Puncte de contact. -->
    <div class="ncrm-privacy-activity__row">
      <span class="ncrm-privacy-activity__label">
        <v-icon size="16" class="ncrm-privacy-activity__icon">mdi-email-sync-outline</v-icon>
        {{ $t(`${copyKey}.mailjetFailedPoints`) }}
      </span>
      <v-btn v-if="activity.mailjet_sync_failed_points > 0" size="small" variant="text" color="error"
        @click="$emit('open-failed-sync')">
        {{ $t(`${copyKey}.mailjetFailedPointsOpen`, { count: activity.mailjet_sync_failed_points }) }}
      </v-btn>
      <strong v-else>{{ display(activity.mailjet_sync_failed_points) }}</strong>
    </div>
  </section>
</template>

<script>
import NcrmPrivacyStackedBar from "./NcrmPrivacyStackedBar.vue";
import { formatDisplayNumber } from "@/Utile/displayFormat.mjs";

export default {
  name: "NcrmPrivacyActivityCard",
  components: { NcrmPrivacyStackedBar },
  emits: ["open-failed-sync"],
  props: {
    activity: { type: Object, default: () => ({}) },
    // `null` = surse indisponibile, nu „niciun eveniment".
    eventsBySource: { type: Object, default: null },
    periodDays: { type: Number, default: 30 },
    unavailable: { type: Array, default: () => [] },
  },
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.workspace.activity"; },
    sourcesUnavailable() { return this.eventsBySource === null || this.unavailable.includes("events_by_source"); },
    sources() {
      const entries = Object.entries(this.eventsBySource || {}).filter(([, count]) => count > 0);
      const max = Math.max(0, ...entries.map(([, count]) => count));
      return entries.map(([key, count]) => ({
        key,
        count,
        widthPct: max ? (count / max) * 100 : 0,
        segment: { key, pct: 100, color: "rgb(var(--v-theme-quaternary))" },
      }));
    },
  },
  methods: {
    // `null` înseamnă „indisponibil", nu zero.
    display(value) {
      return value === null || value === undefined
        ? "—"
        : formatDisplayNumber(Number(value));
    },
  },
};
</script>

<style scoped>
.ncrm-privacy-activity__headline { display: flex; flex-direction: column; gap: 2px; }
.ncrm-privacy-activity__value { font-size: 28px; font-weight: 700; line-height: 1.1; }
.ncrm-privacy-activity__sources {
  display: grid; grid-template-columns: minmax(70px, auto) minmax(0, 1fr) 32px; gap: 8px 10px; align-items: center; font-size: 13.5px;
}
.ncrm-privacy-activity__count { text-align: right; }
.ncrm-privacy-activity__row { display: flex; justify-content: space-between; align-items: center; gap: 12px; min-height: 32px; font-size: 14px; }
.ncrm-privacy-activity__label { display: flex; align-items: center; gap: 8px; }
.ncrm-privacy-activity__icon { color: rgb(var(--v-theme-quaternary)); }
.ncrm-privacy-activity__status { display: flex; align-items: center; gap: 6px; font-weight: 600; }
.ncrm-privacy-activity__status--ok { color: rgb(var(--v-theme-success)); }
.ncrm-privacy-activity__status--error { color: rgb(var(--v-theme-error)); }
</style>
